// Import statements
import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Collapse } from '@mui/material';
import useActiveLink from '../../../hooks/useActiveLink';
import NavItem from './NavItem';
import { path, ROOTS_DASHBOARD } from 'src/routes/paths';

// PropTypes definition
NavList.propTypes = {
  data: PropTypes.object,
  depth: PropTypes.number,
  hasChild: PropTypes.bool,
};

// Main component
export default function NavList({ data, depth, hasChild }) {
  const { active, isExternalLink } = useActiveLink(path(ROOTS_DASHBOARD, data.path), hasChild ? false : true, hasChild);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const sublistActive = useMemo(() => {
    return data.children && data.children.some((child) => useActiveLink(path(ROOTS_DASHBOARD, child.path)).active);
  }, [data.children]);

  const handleToggle = () => {
    if (data.children.length) {
      setOpen(!open);
    }
  };

  const handleClose = useCallback(() => {
    if (!isExternalLink || !data.children || !sublistActive) {
      setOpen(false);
    }
  }, [isExternalLink, data.children, sublistActive]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRef.current.contains(event.target)) {
        if (!data.children.length) {
          setOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [data.children, isExternalLink, sublistActive]);
  return (
    <>
      <div ref={dropdownRef}>
        <NavItem
          item={data}
          depth={depth}
          open={open}
          active={active}
          isExternalLink={isExternalLink}
          onClick={handleToggle}
          onClose={handleClose}
          hasChild={hasChild}
        />

        {hasChild > 0 && (
          <Collapse in={open} unmountOnExit>
            <NavSubList data={data.children} depth={depth} hasChild={hasChild} />
          </Collapse>
        )}
      </div>
    </>
  );
}

// PropTypes definition for NavSubList
NavSubList.propTypes = {
  data: PropTypes.array,
  depth: PropTypes.number,
};

// Nested component for handling sub-lists
function NavSubList({ data, depth, hasChild }) {
  return (
    <>
      {data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={hasChild > 0}
        />
      ))}
    </>
  );
}